if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      connectedCallback() {
        super.connectedCallback();
        if (!this.modalContent) this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.ensureCloseButton();
        if (this.alignOnResize) return;
        this.alignOnResize = () => {
          if (this.hasAttribute('open')) this.alignCloseButton();
        };
        window.addEventListener('resize', this.alignOnResize);
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
            this.ensureCloseButton();
            this.scheduleAlignCloseButton();
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.placeViewDetails(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      ensureCloseButton() {
        const closeButton = this.querySelector('.quick-add-modal__toggle');
        if (!closeButton || closeButton.dataset.e2lCloseBound === 'true') return;
        closeButton.dataset.e2lCloseBound = 'true';
        closeButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.hide(false);
        });
      }

      scheduleAlignCloseButton() {
        const align = () => this.alignCloseButton();
        requestAnimationFrame(() => requestAnimationFrame(align));
        setTimeout(align, 60);
        setTimeout(align, 300);
        const info = this.modalContent?.querySelector('.product__info-container');
        if (!info || typeof ResizeObserver === 'undefined') return;
        this.alignObserver?.disconnect();
        this.alignObserver = new ResizeObserver(() => {
          if (this.hasAttribute('open')) this.alignCloseButton();
        });
        this.alignObserver.observe(info);
      }

      alignCloseButton() {
        const content = this.querySelector('.quick-add-modal__content');
        const toggle = this.querySelector('.quick-add-modal__toggle');
        const edge =
          this.modalContent?.querySelector('.product-form__input .select') ||
          this.modalContent?.querySelector('.product-form__submit');
        if (!content || !toggle || !edge) return;

        const contentBox = content.getBoundingClientRect();
        const edgeBox = edge.getBoundingClientRect();
        if (contentBox.width < 1 || edgeBox.width < 1) return;

        const row = this.modalContent.querySelector('.e2l-qv-top .product__text') || this.modalContent.querySelector('.e2l-qv-top');
        const rowBox = row ? row.getBoundingClientRect() : edgeBox;
        const top = rowBox.top + rowBox.height / 2 - toggle.offsetHeight / 2 - contentBox.top;

        toggle.style.position = 'absolute';
        toggle.style.left = 'auto';
        toggle.style.right = `${Math.max(0, Math.round(contentBox.right - edgeBox.right))}px`;
        toggle.style.top = `${Math.max(0, Math.round(top))}px`;
      }

      placeViewDetails(productElement) {
        const info = productElement.querySelector('.product__info-container');
        const details = productElement.querySelector('.product__view-details');
        const vendor = info?.querySelector('.product__text.caption-with-letter-spacing');
        if (!info || !details || !vendor) return;

        const row = document.createElement('div');
        row.className = 'e2l-qv-top';
        vendor.replaceWith(row);
        row.append(vendor, details);
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
